<script setup lang="ts">
import { computed } from "vue";
import { LazyTooltip } from "@/components/ui/tooltip";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import type { SearchLivePollState } from "@/composables/api/useSearchResultsLivePolling";

/**
 * Toolbar dot showing whether the search results are live-polling — the
 * single surface for polling health (there are deliberately no toasts).
 * It's a bare colored dot: click it to open a popover explaining the state
 * (payload size vs. the limit, poll cadence, last successful refresh). A
 * click-triggered popover (not a hover tooltip) keeps it usable on touch
 * and out of the way of the dense toolbar.
 */
const props = defineProps<{
  state: SearchLivePollState;
  payloadBytes: number;
  maxPayloadBytes: number;
  pollIntervalMs: number;
  /** Epoch ms of the last successful refresh (0 before any data). */
  lastUpdatedAt: number;
}>();

function formatBytes(bytes: number): string {
  if (bytes >= 1_000_000) {
    return `${(bytes / 1_000_000).toFixed(1)} MB`;
  }
  if (bytes >= 1_000) {
    return `${Math.round(bytes / 1_000)} KB`;
  }
  return `${bytes} B`;
}

const intervalSeconds = computed(() => Math.round(props.pollIntervalMs / 1000));

const lastRefreshLabel = computed(() =>
  props.lastUpdatedAt > 0
    ? new Date(props.lastUpdatedAt).toLocaleTimeString()
    : null,
);

const dotClass = computed(
  () =>
    ({
      active: "bg-emerald-500",
      // A failing refresh is a problem the user should notice — red, never hidden.
      failing: "bg-red-500",
      "payload-too-large": "bg-muted-foreground/50",
      // Nothing to poll yet: a muted dot holds the spot instead of vanishing.
      idle: "bg-muted-foreground/40",
    })[props.state],
);

const ariaLabel = computed(
  () =>
    ({
      active: "Live refresh on. Click for details.",
      failing: "Live refresh failing. Click for details.",
      "payload-too-large": "Live refresh off. Click for details.",
      idle: "Live refresh idle. Click for details.",
    })[props.state],
);

/** The dot's tooltip: its state in a few words, such as "Live refresh on". A click shows the details. */
const tooltipText = computed(() => ariaLabel.value.replace(/\. Click for details\.$/, ""));

const tooltipLines = computed<string[]>(() => {
  const payload = formatBytes(props.payloadBytes);
  const limit = formatBytes(props.maxPayloadBytes);
  switch (props.state) {
    case "active":
      return [
        `Refreshing these results every ${intervalSeconds.value}s.`,
        `Result payload ${payload} — under the ${limit} live-refresh limit.`,
        ...(lastRefreshLabel.value
          ? [`Last refreshed ${lastRefreshLabel.value}.`]
          : []),
      ];
    case "payload-too-large":
      return [
        "Live refresh is off for this result set.",
        `Result payload ${payload} is over the ${limit} limit.`,
      ];
    case "failing":
      return [
        "Live refresh is failing — values shown may be stale. Retrying automatically.",
        ...(lastRefreshLabel.value
          ? [`Last successful refresh ${lastRefreshLabel.value}.`]
          : []),
      ];
    case "idle":
      return [
        "No results to live-refresh yet.",
        "Live values appear once rows load.",
      ];
    default:
      return [];
  }
});
</script>

<template>
  <!-- The tooltip wraps the whole menu, not just its trigger. See LazyTooltip for why. -->
  <LazyTooltip>
    <Popover>
      <PopoverTrigger as-child>
        <button
          type="button"
          data-testid="search-live-polling-indicator"
          :data-state="state"
          :aria-label="ariaLabel"
          class="inline-flex items-center justify-center rounded p-1"
        >
          <span
            class="size-2 shrink-0 rounded-full"
            :class="[dotClass, state === 'active' && 'animate-pulse']"
            aria-hidden="true"
          />
        </button>
      </PopoverTrigger>
      <PopoverContent
        side="bottom"
        align="end"
        class="w-auto max-w-64 space-y-1 p-3 text-xs"
      >
        <p v-for="line in tooltipLines" :key="line">{{ line }}</p>
      </PopoverContent>
    </Popover>
    <template #content>{{ tooltipText }}</template>
  </LazyTooltip>
</template>
